import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "./AuthContext";
import { d } from "../lib/dialer";

// Centro de Llamadas V4 (SIMULADO) -- controlador de sesión de la burbuja.
// Vive POR ENCIMA de las páginas (se monta en App.js junto a NotificationProvider), así que
// navegar entre módulos nunca lo desmonta. NO crea llamadas: solo LEE `/dialer/bubble`
// (el backend es la autoridad; cada lectura vuelve a validar el permiso del usuario).
//
// Reconciliación: `reconciled` es true solo después de una lectura EXITOSA reciente. Si se
// pierde la conexión o se recarga el panel, vuelve a false y la burbuja bloquea las
// acciones nuevas hasta confirmar el estado real. Nunca se asume que una llamada terminó
// solo porque la pantalla se refrescó o dejó de responder.

const Ctx = createContext(null);
export const useCallBubble = () => useContext(Ctx);

const CRM_ROLES = ["admin", "gerente", "asistente", "bilingue"];
const FAST_MS = 3000;   // hay llamada viva
const IDLE_MS = 20000;  // nada vivo: sondeo espaciado
const KEY_EXPANDED = "dialer.bubble.expanded";
const KEY_SELECTED = "dialer.bubble.selected";
const KEY_DISMISSED = "dialer.bubble.dismissedEnded";

const ss = {
  get: (k) => { try { return window.sessionStorage.getItem(k); } catch (e) { return null; } },
  set: (k, v) => { try { if (v == null) window.sessionStorage.removeItem(k); else window.sessionStorage.setItem(k, v); } catch (e) { /* sin almacenamiento */ } },
};

export function CallBubbleProvider({ children }) {
  const { user } = useAuth();
  const role = (user?.role || "").toLowerCase();
  const enabled = !!user && CRM_ROLES.includes(role);

  const [calls, setCalls] = useState([]);
  const [lastEnded, setLastEnded] = useState(null);
  const [observed, setObserved] = useState([]);  // pareja fija (solo lectura): [{station_id, mode: aviso|burbuja, ...}]
  const [reconciled, setReconciled] = useState(false);
  const [offline, setOffline] = useState(false);
  const [expanded, setExpandedState] = useState(ss.get(KEY_EXPANDED) === "1");
  const [selectedId, setSelectedState] = useState(ss.get(KEY_SELECTED));
  const [dismissedEnded, setDismissedState] = useState(ss.get(KEY_DISMISSED));
  const timer = useRef(null);
  const inflight = useRef(false);
  const liveRef = useRef(false);
  const mounted = useRef(true);

  const setExpanded = useCallback((v) => { ss.set(KEY_EXPANDED, v ? "1" : "0"); setExpandedState(v); }, []);
  const setSelectedId = useCallback((v) => { ss.set(KEY_SELECTED, v); setSelectedState(v); }, []);
  const dismissEnded = useCallback((id) => { ss.set(KEY_DISMISSED, id); setDismissedState(id); }, []);

  const refresh = useCallback(async () => {
    if (!enabled || inflight.current) return;
    inflight.current = true;
    try {
      const r = await d.get("/bubble");
      if (!mounted.current) return;
      setCalls(r.data?.calls || []);
      setLastEnded(r.data?.last_ended || null);
      let obs = [];
      if (role === "bilingue") {
        // Vista de observación de la pareja asistente (solo lectura; el servidor decide qué se ve).
        try { obs = (await d.get("/observer")).data?.stations || []; } catch (e) { obs = []; }
      }
      if (!mounted.current) return;
      setObserved(obs);
      liveRef.current = (r.data?.calls || []).length > 0 || obs.some((o) => o.mode === "burbuja");
      setReconciled(true); setOffline(false);
    } catch (e) {
      if (!mounted.current) return;
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        // Se perdió el acceso (reasignación, revocación, sesión): se limpia, no se muestra nada ajeno.
        setCalls([]); setLastEnded(null); setObserved([]); liveRef.current = false; setReconciled(true); setOffline(false);
      } else {
        // Sin red / servidor caído: se CONSERVA lo último conocido pero ya NO está reconciliado.
        setReconciled(false); setOffline(true);
      }
    } finally { inflight.current = false; }
  }, [enabled, role]);

  // Sondeo adaptativo. Cada vuelta re-valida el permiso en el servidor.
  useEffect(() => {
    mounted.current = true;
    if (!enabled) { setCalls([]); setLastEnded(null); setReconciled(false); return undefined; }
    let stop = false;
    const loop = async () => {
      if (stop) return;
      if (!document.hidden) await refresh();
      if (stop) return;
      // El bilingüe pareja depende de detectar en vivo lo que hace OTRO usuario (su asistente) --
      // no tiene forma de dispararse su propio refresco al vuelo como sí la tiene quien actúa
      // directamente (notifyDialerChanged). Por eso sondea siempre rápido, nunca al ritmo lento.
      const fast = liveRef.current || offline || role === "bilingue";
      timer.current = setTimeout(loop, fast ? FAST_MS : IDLE_MS);
    };
    loop();
    const kick = () => { if (!document.hidden) refresh(); };
    document.addEventListener("visibilitychange", kick);
    window.addEventListener("focus", kick);
    window.addEventListener("online", kick);
    window.addEventListener("dialer:changed", kick);
    return () => {
      stop = true; mounted.current = false; clearTimeout(timer.current);
      document.removeEventListener("visibilitychange", kick);
      window.removeEventListener("focus", kick);
      window.removeEventListener("online", kick);
      window.removeEventListener("dialer:changed", kick);
    };
  }, [enabled, refresh, offline, role]);

  // La llamada que se muestra: la elegida si sigue viva; si no, una conectada; si no, la primera.
  const current = calls.find((c) => c.id === selectedId) || calls.find((c) => c.connected) || calls[0] || null;
  const ended = !current && lastEnded && lastEnded.id !== dismissedEnded ? lastEnded : null;

  const value = {
    enabled, calls, observed, current, ended, reconciled, offline, expanded, setExpanded, selectedId, setSelectedId, dismissEnded, refresh,
    // Con la burbuja sin reconciliar, las acciones nuevas se bloquean (la UI lo consulta).
    canAct: reconciled && !offline,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
